/**
 * PERSONAL timetable (F3-A): weekly view of MY sessions (student via
 * enrollment, teacher via cohort_teacher — both arrive via sync), "today"
 * list, and ICS calendar download. Local-first over my_cohorts_*.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { useNavigate } from "@tanstack/solid-router";
import { CalendarDays, ChevronLeft, ChevronRight, Download, ListChecks, Video } from "@fleet/icons";
import { joinWindowState, localDateKey } from "@space/shared";
import type { ClassSession } from "@space/powersync";
import { downloadScheduleIcs } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { useMySessions } from "../hooks/use-schedule-data";
// M5: link to the class plan (flipped classroom), gated by feature.
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";
// F3-G: LIVE badge + enter the class + view recording.
import { EnVivoBadge } from "@/features/live/components/en-vivo-badge";
import { RecordingPlayer } from "@/features/live/components/recording-player";
import { useNow } from "@/features/live/hooks/use-now";

/** Monday (ISO) of the week that `d` falls in. */
function weekMonday(d: Date): Date {
  const copy = new Date(d);
  const dow = (copy.getDay() + 6) % 7; // 0 = Monday
  copy.setDate(copy.getDate() - dow);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function ymd(d: Date): string {
  // LOCAL date: toISOString() gives the UTC date and in UTC+ time zones
  // local midnight falls on "yesterday" (COV — same family as the session generator).
  return localDateKey(d);
}

function dayLabel(d: Date): string {
  return new Intl.DateTimeFormat(undefined, { weekday: "short", day: "numeric" }).format(d);
}

export const ScheduleView: Component = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const now = useNow();
  const [anchor, setAnchor] = createSignal(new Date());

  const enterLive = (sessionId: string) => {
    void navigate({
      to: "/live/$sessionId",
      params: { sessionId },
    });
  };

  // M5: is the flipped classroom turned on in the active org? (UX only; the
  // route is also gated by withFeatureGate).
  const flipped = useFeatureEnabled("academy.flipped_sessions");
  const openPlan = (sessionId: string) => {
    void navigate({
      to: "/schedule/$sessionId",
      params: { sessionId },
    });
  };

  const monday = createMemo(() => weekMonday(anchor()));
  const days = createMemo(() => {
    const start = monday();
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
  });

  const sessions = useMySessions(() => {
    const start = monday();
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return { from: ymd(start), to: ymd(end) };
  });

  const byDay = createMemo(() => {
    const map = new Map<string, ClassSession[]>();
    for (const s of sessions.data ?? []) {
      const key = s.date ?? "";
      const arr = map.get(key) ?? [];
      arr.push(s);
      map.set(key, arr);
    }
    for (const [key, arr] of map) {
      map.set(
        key,
        arr.toSorted((a, b) => (a.start_time ?? "").localeCompare(b.start_time ?? "")),
      );
    }
    return map;
  });

  const todayKey = ymd(new Date());
  const todaySessions = createMemo(() => byDay().get(todayKey) ?? []);

  const shift = (weeks: number) => {
    const d = new Date(anchor());
    d.setDate(d.getDate() + weeks * 7);
    setAnchor(d);
  };

  const downloadIcs = async () => {
    try {
      await downloadScheduleIcs();
    } catch {
      showToast(t(msg`Could not download the calendar`), "error");
    }
  };

  return (
    <div class="mx-auto max-w-4xl space-y-5 p-4 md:p-6" data-testid="schedule-view">
      <div class="flex items-center justify-between">
        <h1 class="flex items-center gap-2 font-display text-2xl font-extrabold">
          <CalendarDays class="h-6 w-6 text-primary" /> {t(msg`My schedule`)}
        </h1>
        <div class="flex items-center gap-1">
          <Button
            variant="outline"
            data-testid="download-ics"
            onClick={() => void downloadIcs()}
            class="gap-1"
          >
            <Download class="h-4 w-4" /> {t(msg`Export ICS`)}
          </Button>
        </div>
      </div>

      {/* TODAY's sessions */}
      <section class="space-y-2 rounded-2xl border border-border p-4" data-testid="today-sessions">
        <h2 class="text-sm font-bold">{t(msg`Today`)}</h2>
        <Show
          when={todaySessions().length > 0}
          fallback={<p class="text-sm text-muted-foreground">{t(msg`No classes today.`)}</p>}
        >
          <ul class="space-y-1">
            <For each={todaySessions()}>
              {(s) => {
                const times = () => ({
                  date: s.date,
                  startTime: s.start_time,
                  endTime: s.end_time,
                });
                const isLive = () => joinWindowState(now(), times()).isLiveNow;
                return (
                  <li
                    class="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-muted/30 px-3 py-2 text-sm"
                    data-testid="today-session-row"
                  >
                    <span class="flex items-center gap-2 font-semibold">
                      {(s.start_time ?? "").slice(0, 5)}–{(s.end_time ?? "").slice(0, 5)}
                      <span class="font-normal text-muted-foreground">
                        {s.topic ?? t(msg`Class`)}
                      </span>
                      <EnVivoBadge times={times()} />
                    </span>
                    <span class="flex items-center gap-2">
                      <Show when={flipped().enabled}>
                        <button
                          type="button"
                          class="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-semibold hover:bg-muted"
                          data-testid="today-open-plan"
                          onClick={() => openPlan(s.id)}
                        >
                          <ListChecks class="h-3.5 w-3.5" /> {t(msg`Plan`)}
                        </button>
                      </Show>
                      <Show when={isLive()}>
                        <button
                          type="button"
                          class="inline-flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1 text-xs font-bold text-primary-foreground"
                          data-testid="today-enter-live"
                          onClick={() => enterLive(s.id)}
                        >
                          <Video class="h-3.5 w-3.5" /> {t(msg`Enter`)}
                        </button>
                      </Show>
                      <Show when={s.recording_video_id}>
                        <RecordingPlayer
                          videoId={s.recording_video_id!}
                          title={s.topic ?? undefined}
                        />
                      </Show>
                    </span>
                  </li>
                );
              }}
            </For>
          </ul>
        </Show>
      </section>

      {/* Week navigation */}
      <div class="flex items-center justify-between">
        <button
          type="button"
          data-testid="week-prev"
          onClick={() => shift(-1)}
          class="rounded-lg p-2 hover:bg-muted"
          aria-label={t(msg`Previous week`)}
        >
          <ChevronLeft class="h-5 w-5" />
        </button>
        <span class="text-sm font-semibold">
          {ymd(days()[0]!)} → {ymd(days()[6]!)}
        </span>
        <button
          type="button"
          data-testid="week-next"
          onClick={() => shift(1)}
          class="rounded-lg p-2 hover:bg-muted"
          aria-label={t(msg`Next week`)}
        >
          <ChevronRight class="h-5 w-5" />
        </button>
      </div>

      {/* Weekly grid */}
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-7">
        <For each={days()}>
          {(d) => {
            const key = ymd(d);
            const list = () => byDay().get(key) ?? [];
            return (
              <div
                class={`min-h-[6rem] rounded-xl border p-2 ${
                  key === todayKey ? "border-primary bg-primary/5" : "border-border"
                }`}
                data-testid="week-day"
                data-date={key}
              >
                <p class="mb-1 text-xs font-bold text-muted-foreground">{dayLabel(d)}</p>
                <For each={list()}>
                  {(s) => (
                    <button
                      type="button"
                      class="mb-1 block w-full rounded-md bg-accent/12 px-2 py-1 text-left text-xs enabled:hover:bg-accent/20"
                      data-testid="week-session"
                      disabled={!flipped().enabled}
                      onClick={() => openPlan(s.id)}
                    >
                      <p class="font-semibold text-accent">{(s.start_time ?? "").slice(0, 5)}</p>
                      <p class="truncate text-muted-foreground">{s.topic ?? t(msg`Class`)}</p>
                    </button>
                  )}
                </For>
              </div>
            );
          }}
        </For>
      </div>
    </div>
  );
};
